import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Users, ListPlus, Zap, FileBarChart, ArrowRight, ArrowLeft, Sparkles, X, Cloud } from "lucide-react";

const STORAGE_KEY = "adsfunnel_tour_completed_v1";

const STEPS = [
  {
    id: "welcome",
    icon: Sparkles,
    title: "Bienvenido a adsFunnel",
    body:
      "Te mostramos en 4 pasos cómo extraer toda la información útil de los anuncios de tus competidores en Meta, LinkedIn y Google Ads — todo en un único informe consolidado.",
  },
  {
    id: "drive",
    icon: Cloud,
    title: "1. Conecta tu Google Drive",
    body:
      "Antes de lanzar tu primer análisis necesitas conectar Google Drive desde Configuración → Google. Los informes y todos los creativos descargados (videos, imágenes, transcripciones) se guardan automáticamente en una carpeta 'adsFunnel' de tu Drive. Sin Drive conectado, los análisis no se pueden lanzar.",
    cta: { label: "Ir a Configuración", to: "/settings" },
  },
  {
    id: "competitors",
    icon: ListPlus,
    title: "2. Agrega competidores",
    body:
      "Ve a 'Competidores' y añade los que quieras monitorear. Para cada uno, pega la URL de su biblioteca de anuncios en Meta, LinkedIn o Google. Te damos enlaces directos a las librerías oficiales para que los encuentres rápido.",
    cta: { label: "Ir a Competidores", to: "/competitors" },
  },
  {
    id: "launch",
    icon: Zap,
    title: "3. Lanza un análisis",
    body:
      "Desde el Dashboard pulsa 'Lanzar nuevo análisis' y elige los competidores activos. El sistema rastrea cada plataforma, descarga creativos a tu Google Drive y los pasa a la IA.",
  },
  {
    id: "report",
    icon: FileBarChart,
    title: "4. Recibe tu informe",
    body:
      "La IA analiza copy, hooks, formatos y estrategia de cada anuncio y genera un informe consolidado en Google Docs con recomendaciones accionables. Encuéntralo en 'Historial' y en tu Google Drive (carpeta 'adsFunnel').",
    cta: { label: "Empezar ahora", to: "/settings" },
  },
];

export default function WelcomeTour() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const seen = localStorage.getItem(STORAGE_KEY);
    if (!seen) {
      // small delay so the modal appears after the dashboard rendered
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, []);

  const close = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setOpen(false);
    setStep(0);
  };

  const next = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
    else close();
  };
  const prev = () => step > 0 && setStep(step - 1);

  const current = STEPS[step];
  const Icon = current.icon;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent
        className="bg-slate-900 border-white/10 text-white max-w-lg p-0 overflow-hidden"
        data-testid="welcome-tour"
      >
        <button
          onClick={close}
          className="absolute top-3 right-3 text-slate-500 hover:text-white transition-colors z-10"
          aria-label="Cerrar tour"
          data-testid="welcome-tour-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Brand gradient banner */}
        <div className="brand-gradient h-1.5" />

        <div className="p-8">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 rounded-xl brand-gradient flex items-center justify-center">
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest font-semibold">
                {step === 0 ? "Tour" : `Paso ${step} de ${STEPS.length - 1}`}
              </p>
              <h2 className="text-xl font-bold text-white">{current.title}</h2>
            </div>
          </div>

          <p className="text-sm text-slate-300 leading-relaxed mb-6">{current.body}</p>

          {/* Step dots */}
          <div className="flex items-center gap-1.5 mb-6">
            {STEPS.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setStep(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? "w-8 brand-gradient" : "w-1.5 bg-slate-700 hover:bg-slate-600"
                }`}
                aria-label={`Paso ${i + 1}`}
              />
            ))}
          </div>

          <div className="flex items-center justify-between gap-3">
            <Button
              onClick={prev}
              variant="ghost"
              disabled={step === 0}
              className="text-slate-400 hover:text-white disabled:opacity-30"
              data-testid="welcome-tour-prev"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Anterior
            </Button>

            <div className="flex items-center gap-2">
              <button
                onClick={close}
                className="text-xs text-slate-500 hover:text-slate-300"
                data-testid="welcome-tour-skip"
              >
                Saltar
              </button>
              <Button
                onClick={next}
                className="bg-cyan-600 hover:bg-cyan-500 text-white"
                data-testid="welcome-tour-next"
              >
                {step === STEPS.length - 1 ? "¡Empezar!" : "Siguiente"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
